import React from 'react';
import PropTypes from 'prop-types';
import { ArrowRight, UserPlus, UserMinus, Loader2 } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';

function ConfirmChangesDialog({ isOpen, changes, onConfirm, onCancel, isSaving }) {
  if (!changes) return null;

  const { nameChanged, addedHeads, removedHeads } = changes;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="sm:max-w-md" onClose={onCancel}>
        <DialogHeader>
          <DialogTitle>Confirm Changes</DialogTitle>
          <DialogDescription>Please review the following changes before saving.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Department Name Change */}
          {nameChanged && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Department Name</p>
              <div className="flex items-center gap-3">
                <span className="px-3 py-1.5 rounded-md bg-muted text-sm truncate max-w-[120px]">
                  {nameChanged.from}
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="px-3 py-1.5 rounded-md bg-primary/10 text-primary text-sm truncate max-w-[120px]">
                  {nameChanged.to}
                </span>
              </div>
            </div>
          )}

          {/* Added Heads */}
          {addedHeads && addedHeads.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-green-600">
                <UserPlus className="h-4 w-4" />
                <span>Added Heads</span>
              </div>
              <div className="space-y-1">
                {addedHeads.map((head) => (
                  <div
                    key={head.id}
                    className="px-3 py-1.5 rounded-md bg-green-500/10 text-green-700 text-sm"
                  >
                    {head.email}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Removed Heads */}
          {removedHeads && removedHeads.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-red-600">
                <UserMinus className="h-4 w-4" />
                <span>Removed Heads</span>
              </div>
              <div className="space-y-1">
                {removedHeads.map((head) => (
                  <div
                    key={head.id}
                    className="px-3 py-1.5 rounded-md bg-red-500/10 text-red-700 text-sm"
                  >
                    {head.email}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Confirm'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

ConfirmChangesDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  changes: PropTypes.shape({
    nameChanged: PropTypes.shape({
      from: PropTypes.string.isRequired,
      to: PropTypes.string.isRequired,
    }),
    addedHeads: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
      }),
    ),
    removedHeads: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        email: PropTypes.string.isRequired,
      }),
    ),
  }),
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSaving: PropTypes.bool.isRequired,
};

ConfirmChangesDialog.defaultProps = {
  changes: null,
};

export default ConfirmChangesDialog;
